'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { MapPin, ChevronRight } from 'lucide-react';
import dynamic from 'next/dynamic';
import { HoloCard } from './HoloCard';
import { StatsRow } from './StatsRow';
import { InfoCard } from './InfoCard';
import { StatusPill } from './StatusPill';
import { ScanHistoryModal } from './ScanHistoryModal';
import { DocumentsCard } from './DocumentsCard';
import { PRODUCT_FALLBACK, type VaultEntry } from '@/lib/vault';
import type { EclipseExperience } from '@/components/map/EclipseMap';

const EclipseMap = dynamic(() => import('@/components/map/EclipseMap'), {
  ssr: false,
  loading: () => (
    <div className="flex-1 flex items-center justify-center" style={{ background: 'var(--pub-surface-2)' }}>
      <div
        style={{
          width: 24,
          height: 24,
          borderRadius: '50%',
          border: '2px solid var(--pub-border)',
          borderTopColor: 'var(--pub-green)',
          animation: 'pub-spin 1s linear infinite',
        }}
      />
    </div>
  ),
});

interface VaultPreviewProps {
  entry: VaultEntry;
}

export function VaultPreview({ entry }: VaultPreviewProps) {
  const t = useTranslations('public');
  const [scanHistoryOpen, setScanHistoryOpen] = useState(false);
  const [sharedLocation, setSharedLocation] = useState<{
    latitude: number;
    longitude: number;
    location: string | null;
  } | null>(null);
  const [mapImageUrl, setMapImageUrl] = useState<string | null>(null);
  const [mapViewOpen, setMapViewOpen] = useState(false);
  const [mapExperiences, setMapExperiences] = useState<EclipseExperience[]>([]);

  // Fetch primary shared location for this code
  useEffect(() => {
    fetch(`/api/codes/${encodeURIComponent(entry.code)}/location`)
      .then((r) => r.json())
      .then((data) => {
        if (data.hasLocation) {
          setSharedLocation({
            latitude: data.latitude,
            longitude: data.longitude,
            location: data.location,
          });
        }
      })
      .catch(() => {});
  }, [entry.code]);

  // Fetch static map thumbnail when we have shared location
  useEffect(() => {
    if (!sharedLocation) return;
    let cancelled = false;

    fetch('/api/map/generate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        lat: sharedLocation.latitude,
        lng: sharedLocation.longitude,
        zoom: 11,
        width: 400,
        height: 180,
        pinColor: 'f59e0b',
      }),
    })
      .then((res) => {
        if (!res.ok) throw new Error();
        return res.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        setMapImageUrl(URL.createObjectURL(blob));
      })
      .catch(() => {});

    return () => { cancelled = true; };
  }, [sharedLocation]);

  // Fetch map experiences when map view opens
  useEffect(() => {
    if (!mapViewOpen) return;
    fetch('/api/eclipse/map?limit=1000')
      .then((r) => r.json())
      .then((data) => setMapExperiences(data.experiences || []))
      .catch(() => {});
  }, [mapViewOpen]);

  const firstSeenFormatted = new Date(entry.firstSeenAt).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  const lastSeenFormatted = new Date(entry.lastSeenAt).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  const productionDateFormatted = entry.productionDate
    ? new Date(entry.productionDate).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  const useByDateFormatted = entry.useByDate
    ? new Date(entry.useByDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null;

  const serial = entry.serialNumber != null ? String(entry.serialNumber).padStart(7, '0') : null;
  const productName = entry.productName || PRODUCT_FALLBACK;

  return (
    <div className="flex flex-col gap-3 px-4" style={{ paddingBottom: 100 }}>
      {/* Holographic certificate */}
      <HoloCard serial={entry.serialNumber} variant="green" title={productName} productName={t('preview.subtitle')} />

      {/* Stats — scan count is tappable */}
      <StatsRow
        items={[
          { value: entry.scanCount, label: t('verified.totalScans'), color: 'var(--pub-green)', onPress: () => setScanHistoryOpen(true) },
          { value: firstSeenFormatted, label: t('verified.firstSeen') },
          { value: t('verified.clean'), label: t('verified.history'), color: 'var(--pub-green)' },
        ]}
      />

      {/* Manufacturing & Traceability data */}
      <InfoCard
        rows={[
          ...(serial ? [{ label: t('compliance.serialNumber'), value: serial, mono: true }] : []),
          ...(entry.batchName ? [{ label: t('verified.batch'), value: entry.batchName, mono: true }] : []),
          ...(productionDateFormatted ? [{ label: t('compliance.manufactured'), value: productionDateFormatted }] : []),
          ...(useByDateFormatted ? [{ label: t('compliance.useBy'), value: useByDateFormatted }] : []),
          ...(entry.manufacturingLot ? [{ label: t('compliance.lot'), value: entry.manufacturingLot, mono: true }] : []),
          ...(entry.productionLine ? [{ label: t('compliance.productionLine'), value: entry.productionLine, mono: true }] : []),
          ...(entry.qualityCheck !== undefined ? [{
            label: t('compliance.qualityCheck'),
            value: <StatusPill variant={entry.qualityCheck ? 'green' : 'red'}>
              {entry.qualityCheck ? t('compliance.passed') : t('compliance.failed')}
            </StatusPill>,
          }] : []),
          ...(entry.operatorName ? [{
            label: t('compliance.operator'),
            value: `${entry.operatorName}${entry.operatorDepartment ? ` · ${entry.operatorDepartment}` : ''}`,
          }] : []),
          ...(entry.clientName ? [{ label: t('compliance.brand'), value: entry.clientName }] : []),
          { label: t('preview.lastSeen'), value: lastSeenFormatted },
          {
            label: t('verified.status'),
            value: <StatusPill variant="green">&#9679; {t('verified.genuine')}</StatusPill>,
          },
          {
            label: t('verified.ownership'),
            value: <StatusPill variant="green">{t('verified.claimed')}</StatusPill>,
          },
        ]}
      />

      {/* Shared location map thumbnail */}
      {sharedLocation && mapImageUrl && (
        <button
          onClick={() => setMapViewOpen(true)}
          className="pub-card w-full relative overflow-hidden block"
          style={{
            padding: 0,
            borderRadius: 'var(--pub-card-radius)',
            height: 120,
            cursor: 'pointer',
          }}
        >
          <img
            src={mapImageUrl}
            alt="Your shared location"
            className="w-full h-full object-cover"
          />
          <div
            className="absolute bottom-2 left-2 flex items-center gap-1.5"
            style={{
              background: 'rgba(0,0,0,0.65)',
              backdropFilter: 'blur(8px)',
              borderRadius: 100,
              padding: '4px 10px',
              fontSize: 10,
              color: '#fff',
              fontFamily: 'var(--font-pub-mono)',
              letterSpacing: '0.04em',
            }}
          >
            <MapPin size={10} className="text-amber-400" />
            {sharedLocation.location || `${sharedLocation.latitude.toFixed(2)}, ${sharedLocation.longitude.toFixed(2)}`}
          </div>
          <div
            className="absolute top-2 right-2 flex items-center gap-1"
            style={{
              background: 'rgba(0,0,0,0.55)',
              backdropFilter: 'blur(8px)',
              borderRadius: 100,
              padding: '3px 8px',
              fontSize: 9,
              color: 'rgba(255,255,255,0.8)',
              fontFamily: 'var(--font-pub-mono)',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}
          >
            View on map
            <ChevronRight size={10} />
          </div>
        </button>
      )}

      {/* Document Access */}
      <DocumentsCard />

      {/* Scan history modal */}
      <ScanHistoryModal
        open={scanHistoryOpen}
        onClose={() => setScanHistoryOpen(false)}
        code={entry.code}
      />

      {/* Fullscreen map view */}
      {mapViewOpen && sharedLocation && (
        <div
          className="fixed inset-0 z-[200] flex flex-col"
          style={{ background: 'var(--pub-surface)' }}
        >
          <div
            className="flex justify-between items-center flex-shrink-0"
            style={{ padding: '52px 20px 12px' }}
          >
            <div>
              <h2
                style={{
                  fontFamily: 'var(--font-pub-display)',
                  fontSize: 18,
                  fontWeight: 700,
                  letterSpacing: '-0.02em',
                  color: 'var(--pub-text-primary)',
                }}
              >
                Your viewing spot
              </h2>
              <p
                style={{
                  fontFamily: 'var(--font-pub-mono)',
                  fontSize: 10,
                  color: 'var(--pub-text-muted)',
                  marginTop: 3,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                }}
              >
                {sharedLocation.location || `${sharedLocation.latitude.toFixed(2)}, ${sharedLocation.longitude.toFixed(2)}`}
              </p>
            </div>
            <button
              onClick={() => setMapViewOpen(false)}
              className="flex items-center justify-center cursor-pointer transition-colors duration-200"
              style={{
                width: 34,
                height: 34,
                borderRadius: 10,
                border: '1px solid var(--pub-border)',
                background: 'var(--pub-surface-2)',
                color: 'var(--pub-text-secondary)',
              }}
              aria-label="Close"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          <div
            className="flex-1 relative overflow-hidden"
            style={{
              margin: '0 16px',
              borderRadius: 20,
              border: '1px solid var(--pub-border)',
            }}
          >
            <EclipseMap
              experiences={mapExperiences}
              highlightedId={entry.code}
              initialCenter={[sharedLocation.latitude, sharedLocation.longitude]}
              className="w-full h-full"
            />
          </div>

          <div className="flex flex-col gap-2 flex-shrink-0" style={{ padding: '14px 16px 32px' }}>
            <button
              onClick={() => setMapViewOpen(false)}
              className="pub-btn pub-btn-secondary"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
